import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Bell, Check } from 'lucide-react';

const WS_URL = (() => {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  return backend.replace(/^http/, 'ws') + '/api/ws-native';
})();

function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/notifications');
    setItems(data.notifications || []); setUnread(data.unread || 0);
  }, []);

  useEffect(() => { load(); }, [load]);

  // WebSocket subscription to user's personal room
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('tm_jwt');
    if (!token) return;
    let closed = false;
    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.event === 'auth' && d.ok) ws.send(JSON.stringify({ type: 'join', project_id: `user:${user.id}` }));
          if (d.event === 'notification' && d.notification) {
            setItems((prev) => [d.notification, ...prev].slice(0, 50));
            setUnread((u) => u + 1);
          }
        } catch { /* noop */ }
      };
      ws.onclose = () => { if (!closed) setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { closed = true; try { wsRef.current?.close(); } catch { /* noop */ } };
  }, [user]);

  // Click outside to close
  useEffect(() => {
    const h = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open]);

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  const clickItem = async (n) => {
    if (!n.read_at) {
      await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.project_id) { nav(`/projects/${n.project_id}`); setOpen(false); }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }} data-testid="notif-bell-wrap">
      <button onClick={() => setOpen((v) => !v)} data-testid="notif-bell" title="Notifications"
        style={{ background: 'transparent', border: 'none', color: 'var(--trello-text-muted)', cursor: 'pointer', padding: 4, position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notif-badge" style={{
            position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff',
            fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 10, minWidth: 12, textAlign: 'center',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <div className="tm-pop" style={{ position: 'absolute', bottom: '100%', left: 0, width: 320, maxHeight: 400, overflowY: 'auto', zIndex: 60, marginBottom: 8 }} data-testid="notif-panel">
          <div className="flex justify-between items-center mb-2">
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Notifications</h4>
            {unread > 0 && <button className="tm-btn-ghost" onClick={markAllRead} data-testid="notif-mark-all" style={{ fontSize: 12 }}><Check size={12} /> Mark all read</button>}
          </div>
          {items.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--trello-text-muted)', fontSize: 13 }}>You&apos;re all caught up ✨</div>}
          <div className="space-y-1">
            {items.map((n) => (
              <button key={n.id} onClick={() => clickItem(n)} data-testid={`notif-item-${n.id}`}
                className={`tm-notif-item ${n.read_at ? 'read' : 'unread'}`}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none',
                  borderRadius: 6, cursor: 'pointer', marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: n.read_at ? 500 : 700, color: 'var(--trello-text)' }}>{n.title}</div>
                {n.message && <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginTop: 2 }}>{n.message}</div>}
                <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 4 }}>{timeAgo(n.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
